import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaEye, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';
import { getSizeCharts, deleteSizeChart } from '../../../services/sizeChartService';

// Styled components
const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const AddButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #4f46e5;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    background-color: #4338ca;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const THead = styled.thead`
  background-color: #f9fafb;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
`;

const Tr = styled.tr`
  &:hover {
    background-color: #f9fafb;
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${props => props.color || '#6b7280'};
  
  &:hover {
    color: ${props => props.hoverColor || '#1f2937'};
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.active ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.active ? '#166534' : '#b91c1c'};
`;

const NoData = styled.div`
  text-align: center;
  padding: 24px;
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
`;

const DialogTitle = styled.h2`
  font-size: 18px;
  margin-top: 0;
  margin-bottom: 16px;
`;

const DialogText = styled.p`
  margin-bottom: 24px;
  color: #6b7280;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const DialogButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  
  background-color: ${props => props.primary ? '#4f46e5' : 'white'};
  color: ${props => props.primary ? 'white' : '#6b7280'};
  border: ${props => props.primary ? 'none' : '1px solid #e5e7eb'};
  
  &:hover {
    background-color: ${props => props.primary ? '#4338ca' : '#f9fafb'};
  }
`;

const ImageThumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
`;

// SizeChartsPage Component
const SizeChartsPage = () => {
  const [sizeCharts, setSizeCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sizeChartToDelete, setSizeChartToDelete] = useState(null);
  
  // Fetch size charts
  useEffect(() => {
    const fetchSizeCharts = async () => {
      try {
        setLoading(true);
        const response = await getSizeCharts();
        if (response.success) {
          setSizeCharts(response.data);
        } else {
          setError('Failed to load size charts');
        }
      } catch (err) {
        console.error('Error fetching size charts:', err);
        setError('Failed to load size charts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSizeCharts();
  }, []);
  
  // Handle delete confirmation
  const handleDeleteClick = (sizeChart) => {
    setSizeChartToDelete(sizeChart);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete action
  const handleDelete = async () => {
    if (!sizeChartToDelete) return;
    
    try {
      const response = await deleteSizeChart(sizeChartToDelete._id);
      if (response.success) {
        // Remove deleted size chart from state
        setSizeCharts(sizeCharts.filter(chart => chart._id !== sizeChartToDelete._id));
        setDeleteDialogOpen(false);
        setSizeChartToDelete(null);
      } else {
        setError(response.error || 'Failed to delete size chart');
      }
    } catch (err) {
      console.error('Error deleting size chart:', err);
      setError('Failed to delete size chart');
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>Size Charts</Title>
        <AddButton to="/admin/size-charts/add">
          <FaPlus size={14} />
          Add Size Chart
        </AddButton>
      </Header>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {loading ? (
        <LoadingSpinner>Loading size charts...</LoadingSpinner>
      ) : sizeCharts.length === 0 ? (
        <NoData>No size charts found. Click "Add Size Chart" to create one.</NoData>
      ) : (
        <Table>
          <THead>
            <tr>
              <Th>Logo</Th>
              <Th>Name</Th>
              <Th>Thumbnail</Th>
              <Th>Status</Th>
              <Th>Created At</Th>
              <Th>Actions</Th>
            </tr>
          </THead>
          <tbody>
            {sizeCharts.map(sizeChart => (
              <Tr key={sizeChart._id}>
                <Td>
                  <ImageThumbnail src={sizeChart.logo.url} alt={sizeChart.name} />
                </Td>
                <Td>{sizeChart.name}</Td>
                <Td>
                  <ImageThumbnail src={sizeChart.thumbnail.url} alt="Size Chart Thumbnail" />
                </Td>
                <Td>
                  <StatusBadge active={sizeChart.isActive}>
                    {sizeChart.isActive ? (
                      <>
                        <FaCheck size={10} />
                        Active
                      </>
                    ) : (
                      <>
                        <FaTimes size={10} />
                        Inactive
                      </>
                    )}
                  </StatusBadge>
                </Td>
                <Td>{new Date(sizeChart.createdAt).toLocaleDateString()}</Td>
                <Td>
                  <ActionButtonsContainer>
                    <ActionButton 
                      as={Link} 
                      to={`/admin/size-charts/view/${sizeChart._id}`}
                      color="#4f46e5"
                      hoverColor="#4338ca"
                      title="View"
                    >
                      <FaEye size={16} />
                    </ActionButton>
                    <ActionButton 
                      as={Link} 
                      to={`/admin/size-charts/edit/${sizeChart._id}`}
                      color="#0ea5e9"
                      hoverColor="#0284c7"
                      title="Edit"
                    >
                      <FaEdit size={16} />
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleDeleteClick(sizeChart)}
                      color="#ef4444"
                      hoverColor="#dc2626"
                      title="Delete"
                    >
                      <FaTrash size={16} />
                    </ActionButton>
                  </ActionButtonsContainer>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      
      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <ConfirmDialog>
          <DialogContent>
            <DialogTitle>Delete Size Chart</DialogTitle>
            <DialogText>
              Are you sure you want to delete the size chart "{sizeChartToDelete?.name}"? This action cannot be undone.
            </DialogText>
            <DialogActions>
              <DialogButton onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </DialogButton>
              <DialogButton primary onClick={handleDelete}>
                Delete
              </DialogButton>
            </DialogActions>
          </DialogContent>
        </ConfirmDialog>
      )}
    </Container>
  );
};

export default SizeChartsPage; 